import React, { useState, useEffect } from 'react';
import axios from 'axios';

function ServerForm({ server, onSave, onCancel }) {
    const [name, setName] = useState('');
    const [gradioUrl, setGradioUrl] = useState('');
    const [fileServerUrl, setFileServerUrl] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (server) {
            setName(server.name || '');
            setGradioUrl(server.gradio_url || '');
            setFileServerUrl(server.file_server_url || '');
        } else {
            setName('');
            setGradioUrl('');
            setFileServerUrl('');
        }
    }, [server]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!name || !gradioUrl || !fileServerUrl) {
            setError('All fields are required.');
            return;
        }
        onSave({
            name: name,
            gradio_url: gradioUrl,
            file_server_url: fileServerUrl
        });
    };

    return (
        <form onSubmit={handleSubmit} className="form-container">
            <h3>{server ? 'Edit Server' : 'Add New Server'}</h3>
            {error && <p className="error-message">{error}</p>}
            <div className="form-group">
                <label>Server Name (Unique)</label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="powerful-gpu-1"
                    disabled={!!server} // Disable name editing
                />
            </div>
            <div className="form-group">
                <label>Gradio API URL</label>
                <input
                    type="text"
                    value={gradioUrl}
                    onChange={(e) => setGradioUrl(e.target.value)}
                    placeholder="http://1.2.3.4:7860"
                />
            </div>
            <div className="form-group">
                <label>File Server URL</label>
                <input
                    type="text"
                    value={fileServerUrl}
                    onChange={(e) => setFileServerUrl(e.target.value)}
                    placeholder="http://1.2.3.4:8080"
                />
            </div>
            <div style={{ display: 'flex', gap: '1rem' }}>
                <button type="submit">Save Server</button>
                {onCancel && <button type="button" className="secondary" onClick={onCancel}>Cancel</button>}
            </div>
        </form>
    );
}

export default function Servers() {
    const [servers, setServers] = useState([]);
    const [editingServer, setEditingServer] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const fetchServers = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/servers/');
            setServers(response.data);
        } catch (err) {
            setError('Failed to fetch servers.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServers();
    }, []);

    const handleSaveServer = async (serverData) => {
        setError('');
        try {
            if (editingServer) {
                await axios.post('/api/servers/edit', serverData);
            } else {
                await axios.post('/api/servers/add', serverData);
            }
            setEditingServer(null);
            fetchServers();
        } catch (err) {
            setError(err.response?.data?.error || `Failed to save server.`);
        }
    };

    const handleRemoveServer = async (name) => {
        try {
            await axios.post('/api/servers/remove', { name: name });
            fetchServers();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to remove server.');
        }
    };

    return (
        <div>
            <h2>Manage Generation Servers</h2>
            
            {editingServer ? (
                <ServerForm 
                    server={editingServer} 
                    onSave={handleSaveServer}
                    onCancel={() => setEditingServer(null)}
                />
            ) : (
                <ServerForm onSave={handleSaveServer} />
            )}

            <h3>Available Servers</h3>
            {error && <p className="error-message">{error}</p>}
            {loading ? <p>Loading...</p> : (
                <ul style={{listStyle: 'none', padding: 0}}>
                    {servers.map(server => (
                        <li key={server.name} style={{background: 'var(--surface-color)', padding: '1rem', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                            <div>
                                <strong>Name:</strong> <code>{server.name}</code><br/>
                                <strong>Gradio:</strong> <code>{server.gradio_url}</code><br/>
                                <strong>Files:</strong> <code>{server.file_server_url}</code>
                            </div>
                            <div style={{display: 'flex', gap: '0.5rem'}}>
                                <button onClick={() => setEditingServer(server)} className="secondary">Edit</button>
                                <button onClick={() => handleRemoveServer(server.name)} className="danger">
                                    Remove
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
} 